/**
 * What matters today, and nothing else.
 *
 * A part-time coach opens this between jobs, on a phone, for about fifteen
 * seconds. So it answers three questions in order: what is next and when, what
 * is waiting on me, and is anything broken. If the answer to the second is
 * nothing, it says so plainly rather than inventing activity to fill the
 * screen.
 */

import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import {
  confirmAssignment,
  isDemoMode,
  listCoachEvents,
  listNotifications,
  listReportsDue,
} from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import { DemoResetButton } from '../../components/DemoResetButton'
import {
  AssignmentBadge,
  AvailabilityBadge,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Section,
  ServiceTag,
  StatTile,
} from '../../components/ui'
import {
  formatDuration,
  formatMonth,
  formatTime,
  periodMonth,
  pluralize,
  relativeTime,
} from '../../lib/format'
import {
  coachSchedule,
  countdownLabel,
  dayLabel,
  greeting,
  useAction,
  useLiveReload,
  useNow,
} from './coachHooks'
import { ActionFeedback, TaskRow } from './coachUi'
import type { CoachEventCard } from '../../lib/api'

export function CoachHome() {
  const { profile } = useAuth()
  const coachId = profile?.id ?? ''
  const now = useNow()

  const { data, error, loading, reload } = useAsync(async () => {
    const [cards, due, notifications] = await Promise.all([
      listCoachEvents(coachId),
      listReportsDue(coachId),
      listNotifications(coachId),
    ])
    return { cards, due, notifications }
  }, [coachId])
  useLiveReload(reload)
  const action = useAction(reload)

  if (loading && !data) return <Loading label="Loading your day" />
  if (error && !data) return <ErrorState message={error} onRetry={reload} />
  if (!data) return null

  const schedule = coachSchedule(data.cards, now)
  const unread = data.notifications.filter((n) => !n.acknowledged_at)
  const firstName = profile?.full_name.split(' ')[0] ?? 'there'
  const hero = schedule.inProgress[0] ?? schedule.next
  const heroId = hero?.event.id
  const thisMonth = schedule.past.filter(
    (c) => c.assignment !== null && periodMonth(c.event.starts_at) === periodMonth(now),
  )

  // The hero already carries its own confirm button, so it is not repeated here.
  const confirmTasks = schedule.needsConfirm.filter((c) => c.event.id !== heroId)
  const taskCount =
    schedule.needsAnswer.length + confirmTasks.length + data.due.length + (unread.length > 0 ? 1 : 0)

  function confirmHero(card: CoachEventCard) {
    const assignment = card.assignment
    if (!assignment) return
    void action.run(
      () => confirmAssignment(assignment.id),
      'Confirmed. Michel can see you are coming.',
    )
  }

  return (
    <>
      <PageHeader
        title={`${greeting(now)}, ${firstName}`}
        subtitle={
          taskCount === 0
            ? 'Nothing is waiting on you.'
            : `${taskCount} ${pluralize(taskCount, 'thing')} waiting on you.`
        }
      />

      {error ? (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Notice tone="warning" title="Showing what was loaded earlier">
            {error}
          </Notice>
        </div>
      ) : null}

      {profile?.status === 'candidate' ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="info" title="You are still in training">
            Finish your modules, then ask Michel to approve you for a service. Events start
            appearing here the moment he does.
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Link className="btn btn--sm" to="/training">
                Go to training
              </Link>
            </div>
          </Notice>
        </div>
      ) : null}

      {/* ----------------------------------------------------------- next up */}

      <Section
        title={schedule.inProgress.length > 0 ? 'Happening now' : 'Next up'}
        action={
          schedule.upcoming.length > 1 ? (
            <Link className="btn btn--ghost btn--sm" to="/events">
              See all
            </Link>
          ) : null
        }
      >
        {hero ? (
          <>
            <NextUpCard card={hero} now={now} />
            {hero.assignment?.status === 'assigned' ? (
              <>
                <p className="small muted" style={{ marginTop: 'var(--sp-3)' }}>
                  You are assigned, confirm you have seen it.
                </p>
                <button
                  type="button"
                  className="btn btn--block"
                  style={{ marginTop: 'var(--sp-3)' }}
                  disabled={action.busy}
                  onClick={() => confirmHero(hero)}
                >
                  {action.busy ? 'Saving' : 'Confirm I will be there'}
                </button>
                <ActionFeedback error={action.error} done={action.done} />
              </>
            ) : null}
          </>
        ) : (
          <EmptyState
            title="Nothing booked"
            body="When Michel asks whether you are free, or puts you on an event, it lands here first."
            action={
              <Link className="btn btn--secondary btn--sm" to="/events">
                See what is open to you
              </Link>
            }
          />
        )}
      </Section>

      {/* ------------------------------------------------------ waiting on you */}

      {taskCount > 0 ? (
        <Section title="Waiting on you" count={taskCount}>
          <div className="stack stack--tight">
            {schedule.needsAnswer.map((card) => (
              <TaskRow
                key={`answer-${card.event.id}`}
                to={`/events/${card.event.id}`}
                title={card.event.title}
                detail={`${dayLabel(card.event.starts_at, now)} at ${formatTime(
                  card.event.starts_at,
                )}. Michel is waiting to hear if you are free.`}
                cta="Answer"
                urgent
              />
            ))}

            {confirmTasks.map((card) => (
              <TaskRow
                key={`confirm-${card.event.id}`}
                to={`/events/${card.event.id}`}
                title={card.event.title}
                detail={`${dayLabel(card.event.starts_at, now)} at ${formatTime(
                  card.event.starts_at,
                )}. You are assigned, confirm you have seen it.`}
                cta="Confirm"
                urgent
              />
            ))}

            {data.due.map((row) => (
              <TaskRow
                key={`report-${row.event.id}`}
                to={`/events/${row.event.id}`}
                title={row.event.title}
                detail={`Worked ${relativeTime(row.event.starts_at, now)}. The report is still open, and it takes two minutes.`}
                cta="Report"
              />
            ))}

            {unread.length > 0 ? (
              <TaskRow
                to="/inbox"
                title={`${unread.length} unread ${pluralize(unread.length, 'reminder')}`}
                detail="Opening one tells Michel it reached you, which keeps you off his chase list."
                cta="Open"
              />
            ) : null}
          </div>
        </Section>
      ) : (
        <Section title="Waiting on you">
          <EmptyState
            title="You are all caught up"
            body="No answers owed, no reports open, no unread reminders. Enjoy it."
          />
        </Section>
      )}

      {/* ------------------------------------------------------------- totals */}

      <Section title="Your month">
        <div className="grid-2">
          <StatTile
            label="Upcoming events"
            value={schedule.upcoming.length}
            to="/events"
            hint={
              schedule.booked.length > 0
                ? `${schedule.booked.length} confirmed`
                : 'Nothing confirmed yet'
            }
          />
          <StatTile
            label="Worked this month"
            value={thisMonth.length}
            hint={formatMonth(now)}
          />
        </div>
        <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
          Earnings stay closed until the pay grid is signed off with Pascal. Until then this is a
          schedule, not a payslip.
        </p>
      </Section>

      {isDemoMode ? (
        <div
          className="row"
          style={{ justifyContent: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-6)' }}
        >
          <span className="faint">Demo data.</span>
          <DemoResetButton label="Start the demo again" />
        </div>
      ) : null}
    </>
  )
}

/**
 * The one event that matters right now, with the countdown a coach actually
 * asks for. The whole card is the link, so the confirm button lives outside it.
 */
function NextUpCard({ card, now }: { card: CoachEventCard; now: Date }) {
  const { event, assignment, availability } = card
  const needsAction = assignment?.status === 'assigned' || availability?.response === 'pending'

  return (
    <Card to={`/events/${event.id}`} tone={needsAction ? 'attention' : 'default'}>
      <p className="faint">
        {dayLabel(event.starts_at, now)} at {formatTime(event.starts_at)}
      </p>
      <p className="card__title" style={{ marginTop: 2 }}>
        {event.title}
      </p>
      <div className="card__meta">
        <ServiceTag service={event.service} />
        <span aria-hidden="true">&middot;</span>
        <span>{formatDuration(event.duration_minutes)}</span>
        {event.location_name ? (
          <>
            <span aria-hidden="true">&middot;</span>
            <span>{event.location_name}</span>
          </>
        ) : null}
      </div>

      <div className="card__foot">
        {assignment ? (
          <AssignmentBadge status={assignment.status} />
        ) : availability ? (
          <AvailabilityBadge response={availability.response} />
        ) : null}
        <span className="spacer" />
        <span className="faint nowrap">
          {countdownLabel(event.starts_at, event.ends_at, now)}
        </span>
      </div>
    </Card>
  )
}
